import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Button,
  Grid,
  Rating,
  Badge
} from '@mui/material';
import {
  ArrowBack,
  Favorite,
  FavoriteBorder,
  Star,
  LocationOn,
  Business,
  Person,
  Spa,
  ShoppingBag
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNav from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import { favoritesAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [favorites, setFavorites] = useState({ providers: [], services: [], products: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // Default to Providers tab

  const tabs = ['Providers', 'Services', 'Products'];

  // Fetch favorites from backend
  const fetchFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await favoritesAPI.getFavorites();
      console.log('🔍 [FAVORITES PAGE] Backend response:', response);
      if (response.success) {
        // Backend returns { favorites: { providers: [], services: [], products: [] } }
        setFavorites(response.data.favorites || response.data);
        console.log('🔍 [FAVORITES PAGE] Set favorites:', response.data.favorites || response.data);
      } else {
        // Fallback to mock data
        console.warn('Backend favorites failed, using mock data');
        setFavorites(mockFavorites);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      // Fallback to mock data on error
      console.warn('Backend favorites failed, using mock data');
      setFavorites(mockFavorites);
    } finally {
      setLoading(false);
    }
  };

  // Remove from favorites
  const removeFromFavorites = async (favoriteId, type) => {
    try {
      console.log('🗑️ [FAVORITES PAGE] Removing favorite:', favoriteId);
      const response = await favoritesAPI.removeFromFavorites(favoriteId);
      console.log('🗑️ [FAVORITES PAGE] Remove response:', response);
      if (response.success) {
        // Refresh favorites after removal
        fetchFavorites();
      } else {
        setError('Failed to remove from favorites');
      }
    } catch (error) {
      console.error('❌ [FAVORITES PAGE] Error removing from favorites:', error);
      setError('Failed to remove from favorites');
    }
  };

  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Mock data for fallback
  const mockFavorites = {
    providers: [
      {
        id: 1,
        type: 'provider',
        name: 'Elegant Beauty Salon',
        rating: 4.8,
        reviewCount: 245,
        distance: 2.5,
        logoUrl: 'https://via.placeholder.com/300x200',
        isFavorited: true
      }
    ],
    services: [
      {
        id: 1,
        type: 'service',
        name: 'Hair Cut & Styling',
        price: 45,
        duration: 60,
        rating: 4.9,
        reviewCount: 156,
        image: 'https://via.placeholder.com/300x200',
        isFavorited: true
      }
    ],
    products: [
      {
        id: 1,
        type: 'product',
        name: 'Organic Face Cream',
        brand: 'Natural Beauty',
        price: 899,
        originalPrice: 1299,
        rating: 4.5,
        reviewCount: 67,
        image: 'https://via.placeholder.com/300x200',
        isFavorited: true
      },
      {
        id: 2,
        type: 'product',
        name: 'Nail Polish Set',
        brand: 'Glamour Nails',
        price: 450,
        originalPrice: 600,
        rating: 4.3,
        reviewCount: 28,
        image: 'https://via.placeholder.com/300x200',
        isFavorited: true
      }
    ]
  };



  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRemoveFavorite = (itemId) => {
    const tabKey = tabs[activeTab].toLowerCase();
    setFavorites(prev => ({
      ...prev,
      [tabKey]: prev[tabKey].filter(item => item.id !== itemId)
    }));
  };

  const getCurrentFavorites = () => {
    const tabKey = tabs[activeTab].toLowerCase();
    return favorites[tabKey] || [];
  };

  const renderProviderCard = (provider) => (
    <Card
      key={provider.id}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 2,
          cursor: 'pointer'
        }
      }}
      onClick={() => navigate(`/provider/${provider.itemId}`)}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            src={provider.logoUrl || provider.image}
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              bgcolor: '#006AF9'
            }}
          >
            {provider.providerType === 'salon' ? (
              <Business sx={{ color: 'white' }} />
            ) : (
              <Person sx={{ color: 'white' }} />
            )}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#15161E',
                mb: 0.5
              }}
            >
              {provider.businessName || 'Unknown Provider'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'grey.600',
                mb: 1
              }}
            >
              {provider.providerType || 'Salon'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Rating value={provider.rating || 5} precision={0.1} size="small" readOnly />
              <Typography variant="body2" sx={{ color: 'grey.600' }}>
                {provider.rating || 5} ({provider.reviewCount || 0} reviews)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn sx={{ fontSize: 16, color: 'grey.600' }} />
              <Typography variant="body2" sx={{ color: 'grey.600' }}>
                {provider.address?.full || `${provider.address?.city || 'Unknown'}, ${provider.address?.state || 'Unknown'}`}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFavorite(provider.id);
            }}
            sx={{ color: '#ff4444' }}
          >
            <Favorite />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  const renderServiceCard = (service) => (
    <Card
      key={service.id}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 2,
          cursor: 'pointer'
        }
      }}
      onClick={() => navigate(`/service/${service.itemId}/${service.providerId}`)}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            src={service.image}
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2
            }}
          >
            <Spa />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#15161E',
                mb: 0.5
              }}
            >
              {service.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'grey.600',
                mb: 1
              }}
            >
              {service.providerName || 'Provider'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Rating value={service.rating || 5} precision={0.1} size="small" readOnly />
              <Typography variant="body2" sx={{ color: 'grey.600' }}>
                {service.rating || 5} ({service.reviewCount || 0} reviews)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ color: '#006AF9', fontWeight: 600 }}>
                ${service.price || 0}
              </Typography>
              {service.duration && <Chip label={`${service.duration} min`} size="small" />}
            </Box>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFavorite(service.id);
            }}
            sx={{ color: '#ff4444' }}
          >
            <Favorite />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  const renderProductCard = (product) => (
    <Card
      key={product.id}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 2,
          cursor: 'pointer'
        }
      }}
      onClick={() => navigate(`/product/${product.itemId}`)}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            src={product.image}
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2
            }}
          >
            <ShoppingBag />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#15161E',
                mb: 0.5
              }}
            >
              {product.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'grey.600',
                mb: 1
              }}
            >
              {product.brand}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Rating value={product.rating} precision={0.1} size="small" readOnly />
              <Typography variant="body2" sx={{ color: 'grey.600' }}>
                {product.rating} ({product.reviewCount} reviews)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ color: '#006AF9', fontWeight: 600 }}>
                ${product.customerSalePrice || product.customerPrice || product.price}
              </Typography>
              {product.customerPrice && product.customerSalePrice && product.customerPrice > product.customerSalePrice && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'grey.500',
                    textDecoration: 'line-through'
                  }}
                >
                  ${product.customerPrice}
                </Typography>
              )}
              {product.customerDiscountPercentage && product.customerDiscountPercentage > 0 && (
                <Chip
                  label={`${product.customerDiscountPercentage}% OFF`}
                  size="small"
                  sx={{ bgcolor: '#ffebee', color: '#d32f2f' }}
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFavorite(product.id);
            }}
            sx={{ color: '#ff4444' }}
          >
            <Favorite />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        textAlign: 'center'
      }}
    >
      <FavoriteBorder sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
      <Typography variant="h6" sx={{ color: 'grey.600', mb: 1 }}>
        No favorites yet
      </Typography>
      <Typography variant="body2" sx={{ color: 'grey.500', mb: 3 }}>
        Start exploring and add your favorite {tabs[activeTab].toLowerCase()}, services, and products
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate('/home')}
        sx={{
          bgcolor: '#006AF9',
          '&:hover': {
            bgcolor: '#0056b3'
          }
        }}
      >
        Start Exploring
      </Button>
    </Box>
  );

  const renderContent = () => {
    const currentFavorites = getCurrentFavorites();

    if (currentFavorites.length === 0) {
      return renderEmptyState();
    }

    switch (activeTab) {
      case 0: // Providers
        return currentFavorites.map(renderProviderCard);
      case 1: // Services
        return currentFavorites.map(renderServiceCard);
      case 2: // Products
        return currentFavorites.map(renderProductCard);
      default:
        return null;
    }
  };

  return (
    <Box sx={{ pb: 8, backgroundColor: '#F1F4F8', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#0A2A66', 
        color: 'white', 
        p: 2,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              p: 1,
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Favorite sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Favourites
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Your saved items and services
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #EEEEEE',
        position: 'sticky',
        top: 64,
        zIndex: 99
      }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'grey.600',
                  minHeight: 60,
                  '&.Mui-selected': {
                    color: '#006AF9',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#006AF9',
                  height: 2
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab key={index} label={tab} />
              ))}
            </Tabs>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2
        }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <BrandLoader />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ py: 2 }}>
            {renderContent()}
          </Box>
        )}
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default FavoritesPage;
